/**
 * Start Here Page - Refactored
 *
 * The introduction page for the Learn system.
 * Now uses reusable components for better maintainability.
 *
 * Components used:
 * - LearnPageLayout: Hero + TOC sidebar layout
 * - ContentSection: Section wrappers
 * - CalloutBox: Highlighted content
 * - OutcomeItem: Benefits list
 * - ConceptBlock: Three building blocks (Intervals, Chords, Progressions)
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    LearnPageLayout,
    ContentSection,
    CalloutBox,
    OutcomeItem,
    ConceptBlock,
} from "../../components/learn";
import { ROUTES } from "../../config/routes";
import { SEO_METADATA } from "../../config/seoMetadata";
import SEO from "../../components/SEO";
import UntrainedVsTrainedDemo from "../demos/UntrainedVsTrainedDemo";
import MelodyIntervalDemo from "../demos/MelodyIntervalDemo";
import ChordQualityDemo from "../demos/ChordQualityDemo";
import ProgressionDemo from "../demos/ProgressionDemo";
import styles from "./StartHere.module.css";

const tocItems = [
    { id: "introduction", label: "Remember that song?" },
    { id: "what-is-ear-training", label: "What is ear training?" },
    { id: "outcomes", label: "What you'll be able to do" },
    { id: "building-blocks", label: "Three building blocks" },
    { id: "getting-started", label: "Where to start" },
];

export default function StartHere() {
    return (
        <>
            <SEO {...SEO_METADATA.LEARN_START} />
            <LearnPageLayout
                title="Start Here"
                subtitle="Your guide to training your ears and understanding music"
                tocItems={tocItems}
            >
            {/* Introduction - The Problem */}
            <ContentSection id="introduction">
                <h2 className={styles.sectionTitle}>Remember that song?</h2>

                <p>
                    You know the one. From your childhood. Or maybe it's that tune you heard last
                    week that's been stuck in your head ever since.
                </p>
                <p>
                    You can hear it perfectly. Every note, every little run. You could hum it
                    right now.
                </p>
                <p>
                    But when you sit down to play it on your instrument, you're hunting. Trying
                    notes. Getting close, but not quite right. The music is <em>right there</em>{" "}
                    in your head, but translating it to your fingers feels like guesswork.
                </p>

                <CalloutBox>
                    <p>
                        Or maybe you hear a remix of your favorite song in your head—a different
                        rhythm, a variation on the melody. You want to bring it to life, but you
                        don't know how to get from what you hear to what you play.
                    </p>
                </CalloutBox>

                <p>
                    That gap between hearing and playing? It's not because you lack talent.
                </p>
                <p>
                    It's because you're missing a skill: <strong>ear training</strong>.
                </p>
                <p>
                    The ability to hear music and understand what's happening. To translate sound
                    into action. And the good news? It's completely learnable.
                </p>
            </ContentSection>

            {/* What is Ear Training */}
            <ContentSection id="what-is-ear-training">
                <h2 className={styles.sectionTitle}>What is ear training?</h2>

                <p>
                    Here's the secret: <strong>Your ears already work perfectly.</strong> You just
                    need to learn the vocabulary.
                </p>
                <p>
                    Ear training is learning to connect what you <em>hear</em> with what you{" "}
                    <em>know</em>. It's naming the sounds. Recognizing the patterns. Understanding
                    how notes relate to each other.
                </p>

                <p>
                    Think of it like learning a language. When you first hear a foreign language,
                    it's just noise. But as you learn, you start picking out words. Then phrases.
                    Then full sentences.
                </p>
                <p>
                    Music is the same. Right now, you might hear a chord progression as
                    just "nice sounds." With ear training, you'll hear it as "I-IV-V-vi in
                    C major"—and you'll know exactly how to play it.
                </p>

                <UntrainedVsTrainedDemo />
            </ContentSection>

            {/* What You'll Be Able To Do */}
            <ContentSection id="outcomes">
                <h2 className={styles.sectionTitle}>Picture yourself three months from now</h2>

                <OutcomeItem title="You hear a melody. You play it.">
                    <p>
                        That song stuck in your head? You sit at your instrument and figure it out.
                        No trial and error. No hunting for notes. You <em>hear</em> the intervals—
                        "perfect fifth up, major third down"—and your fingers go exactly where they
                        need to.
                    </p>
                </OutcomeItem>

                <OutcomeItem title="You recognize chords by their sound">
                    <p>
                        Someone plays a chord. You don't just hear "sound"—you hear "minor
                        seventh." You know the quality, the emotion, the notes. And you can play it
                        back without needing sheet music.
                    </p>
                </OutcomeItem>

                <OutcomeItem title="You follow along in any musical situation">
                    <p>
                        In that jam session? You're not lost anymore. You hear the progression,
                        recognize the pattern—"I-vi-IV-V, classic"—and jump in. Music becomes a
                        conversation you can participate in.
                    </p>
                </OutcomeItem>

                <OutcomeItem title={<>You understand <em>why</em> music sounds the way it does</>}>
                    <p>
                        This is the most powerful part. You won't just recognize sounds—you'll
                        understand the building blocks. Why that progression sounds satisfying. Why
                        this melody feels happy. Music will make <em>sense</em>.
                    </p>
                </OutcomeItem>
            </ContentSection>

            {/* The Three Building Blocks */}
            <ContentSection id="building-blocks">
                <h2 className={styles.sectionTitle}>Three building blocks. That's it.</h2>

                <p>
                    Music—no matter how complex—is built from three fundamental elements. We'll
                    learn them in order, simplest to most complex. Each one builds on the last.
                </p>

                {/* Intervals */}
                <ConceptBlock
                    number={1}
                    title="Intervals"
                    subtitle="The distance between two notes"
                    practiceBox={{
                        label: "What you'll practice",
                        text: "Listening to two notes and identifying the interval. We start with the basics (unison, octave) and work up to all 12 intervals within an octave.",
                    }}
                    demo={<MelodyIntervalDemo />}
                    linkText="Learn about Intervals →"
                    linkTo={ROUTES.LEARN.INTERVALS}
                >
                    <p>
                        This is where everything begins. An interval is the space between two
                        notes. Play C, then E? That's a major third. C then G? Perfect fifth.
                    </p>
                    <p>Every melody you've ever heard is just intervals strung together.</p>
                    <p>
                        Learn to hear intervals, and you can decode any melody. You'll hear "Happy
                        Birthday" and know: major second up, major second up, minor third up. Your
                        brain maps it automatically.
                    </p>
                </ConceptBlock>

                {/* Chords */}
                <ConceptBlock
                    number={2}
                    title="Chords"
                    subtitle="When notes stack together"
                    practiceBox={{
                        label: "What you'll practice",
                        text: "Listening to chords and identifying their quality. Is it major or minor? Is there a seventh? Your ear learns to pick apart the layers.",
                    }}
                    demo={<ChordQualityDemo />}
                    linkText="Learn about Chords →"
                    linkTo={ROUTES.LEARN.CHORDS}
                >
                    <p>
                        Once you understand intervals, chords are the natural next step. A chord is
                        just multiple intervals stacked at the same time.
                    </p>
                    <p>
                        Stack a major third and a minor third? You get a major triad. Different
                        intervals create different qualities: major, minor, diminished, seventh,
                        suspended.
                    </p>
                    <p>
                        Each chord has its own color. Major sounds bright. Minor sounds darker.
                        Diminished sounds tense. Learn to hear these qualities, and you can
                        identify and recreate any chord.
                    </p>
                </ConceptBlock>

                {/* Progressions */}
                <ConceptBlock
                    number={3}
                    title="Progressions"
                    subtitle="How chords move and flow"
                    practiceBox={{
                        label: "What you'll practice",
                        text: "Listening to progressions and identifying the movement. What chords are being played, in what order? How do they relate to the key? Where's the tension and resolution?",
                    }}
                    demo={<ProgressionDemo />}
                    linkText="Learn about Progressions →"
                    linkTo={ROUTES.LEARN.PROGRESSIONS}
                >
                    <p>
                        Now we get to the magic. A progression is chords in sequence, creating
                        movement, tension, and resolution.
                    </p>
                    <p>
                        It's the backbone of every song you know. I-V-vi-IV? Thousands of pop
                        songs. 12-bar blues? I-I-I-I-IV-IV-I-I-V-IV-I-I. Jazz standards? ii-V-I
                        everywhere.
                    </p>
                    <p>
                        Understanding progressions is like understanding story structure. You can
                        predict where music is going. Feel when a song will resolve. Know when the
                        chorus is coming. You understand the <em>narrative</em> of music.
                    </p>
                </ConceptBlock>
            </ContentSection>

            {/* Getting Started */}
            <ContentSection id="getting-started">
                <h2 className={styles.sectionTitle}>Where to start</h2>

                <p>
                    The system is linear. You don't need to know everything at once.
                </p>
                <p>
                    Start with intervals. Get comfortable hearing the distance between two notes.
                    Then move to chords—intervals stacked up. Then progressions—chords in
                    sequence.
                </p>
                <p>
                    Each step makes the next easier. And each step, on its own, makes you a better
                    musician. Progress compounds.
                </p>

                <div className={styles.cta}>
                    <h3 className={styles.ctaTitle}>Ready to begin?</h3>
                    <p className={styles.ctaText}>
                        Start with intervals. It's the foundation everything else is built on.
                    </p>
                    <Link to={ROUTES.LEARN.INTERVALS} className={styles.ctaButton}>
                        Start Learning Intervals
                    </Link>
                    <p className={styles.ctaLinks}>
                        Or jump to <Link to={ROUTES.LEARN.CHORDS}>Chords</Link> or{" "}
                        <Link to={ROUTES.LEARN.PROGRESSIONS}>Progressions</Link> if you're ready
                    </p>
                </div>
            </ContentSection>
        </LearnPageLayout>
        </>
    );
}
